import _ from 'underscore';
import React, { useCallback, useContext, useState } from 'react';
import {
  Body2,
  Button, Caption1,
  Caption1Strong,
  Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle,
  DialogTrigger,
  Spinner,
  makeStyles, mergeClasses, shorthands, tokens,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { I18nContext, I18nModel } from '@bingads-webui/copilot-i18n';
import { CopilotIcon } from '@bingads-webui-react/copilot-ui-base';

import { CreativesList } from './CreativesList';
import { Creative } from '../CreativeCard';
import { FinalUrl } from './FinalUrl';
import { HelpPopup } from '../HelpPopup';

const useCreateDisplayAdDialogStyles = makeStyles({
  surface: {
    ...shorthands.borderWidth('0px'),
    ...shorthands.overflow('hidden'),
  },
  surfaceWithContent: {
    maxWidth: '960px',
    height: '800px',
  },
  body: {
    height: '100%',
  },
  icon: {
    marginRight: '8px',
    display: 'inline',
    paddingTop: 0,
    paddingLeft: 0,
    paddingRight: 0,
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    ...shorthands.gap(tokens.spacingVerticalL),
  },
  finalUrlLabel: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '4px',
    ...shorthands.gap('4px'),
  },
  content: {
    display: 'flex',
    width: '100%',
    justifyContent: 'center',
    alignSelf: 'center',
    overflowY: 'auto',
    flexGrow: 1,
  },
  loadingMessage: {
    width: '100%',
  },
  progressBar: {
    position: 'absolute',
    backgroundColor: tokens.colorNeutralBackground6,
    backgroundImage: 'linear-gradient(90deg, rgba(0, 120, 212, 0) 0, #213AD5 100px, rgba(0, 120, 212, 0) 200px)',
    height: '6px',
    bottom: 0,
    left: 0,
    right: 0,

    animationDuration: '3s',
    animationIterationCount: 'infinite',
    animationName: {
      from: { backgroundPositionX: '0' },
      to: { backgroundPositionX: '600px' },
    },
  },
});

interface ICreateDisplayAdProps {
  finalUrl: string;
  images: Creative[];
  selectedImages: string[];
  isOpen: boolean;
  isLoading: boolean;
  onDismiss: () => void;
  onCreateAds: (selectedImages: string[]) => Promise<void>;
  onFinalUrlUpdate: (finalUrl: string) => void;
  onSelectedImagesUpdate: (selectedImages: string[]) => void;
}

export const CreateDisplayAdDialog: React.FC<ICreateDisplayAdProps> = ({
  finalUrl,
  images,
  selectedImages,
  isLoading,
  isOpen,
  onCreateAds,
  onDismiss,
  onFinalUrlUpdate,
  onSelectedImagesUpdate,
}) => {
  const i18n = useContext(I18nContext) as I18nModel;
  const classes = useCreateDisplayAdDialogStyles();

  const [isCreatingAds, setIsCreatingAds] = useState(false);

  const hasAnyImageSelected = selectedImages.length > 0;

  const handleCreateAds = useCallback(() => {
    if (isCreatingAds) { return; }
    setIsCreatingAds(true);

    onCreateAds(selectedImages).finally(() => setIsCreatingAds(false));
  }, [isCreatingAds, onCreateAds, selectedImages]);

  return (
    <Dialog open={isOpen} onOpenChange={onDismiss}>
      <DialogSurface className={mergeClasses('create-display-ad-dialog', classes.surface, !_.isEmpty(images) && classes.surfaceWithContent)}>
        <DialogBody className={classes.body}>
          <DialogTitle
            action={(
              <DialogTrigger action="close">
                <Button
                  className="close-button"
                  appearance="subtle"
                  aria-label={i18n.getString(_TL_('Close'))}
                  icon={<Dismiss24Regular />}
                />
              </DialogTrigger>
            )}
          >
            <CopilotIcon size="small" color="blue" iconStyles={classes.icon} />
            {i18n.getString(_TL_('Create your display ad with AI'))}
          </DialogTitle>
          <DialogContent>
            <div className={classes.root}>
              <Caption1>
                {i18n.getString(_TL_('Provide your Final URL. Our AI will pull text and images to autogenerate creatives.'))}
              </Caption1>
              <div>
                <div className={classes.finalUrlLabel}>
                  <Caption1Strong>
                    {i18n.getString(_TL_('Final URL'))}
                  </Caption1Strong>
                  <HelpPopup i18n={i18n} topic="pop_BA_NewAdCreate_FinalURL" />
                </div>
                <FinalUrl
                  finalUrl={finalUrl}
                  onFinalUrlEntered={onFinalUrlUpdate}
                />
              </div>
              <div className={classes.content}>
                {!_.isEmpty(images) && (
                  <CreativesList
                    creatives={images}
                    selectionMode="multiple"
                    selected={selectedImages}
                    onSelectedChange={onSelectedImagesUpdate}
                  />
                )}
                {isLoading && (
                  <Body2 className={mergeClasses('loading-message', classes.loadingMessage)}>
                    {i18n.getString(_TL_('Generating creatives...'))}
                  </Body2>
                )}
              </div>
            </div>
          </DialogContent>
          {hasAnyImageSelected && (
            <DialogActions>
              <Button
                className="create-ads-button"
                appearance="primary"
                onClick={handleCreateAds}
              >
                {isCreatingAds ? (
                  <Spinner
                    size="tiny"
                    appearance="inverted"
                    aria-label={i18n.getString(_TL_('Creating ads'))}
                  />
                ) : (
                  i18n.getString(_TL_('Create ads'))
                )}
              </Button>
              <Button
                className="cancel-button"
                onClick={onDismiss}
              >
                {i18n.getString(_TL_('Cancel'))}
              </Button>
            </DialogActions>
          )}
        </DialogBody>
        {isLoading && <div className={classes.progressBar} />}
      </DialogSurface>
    </Dialog>
  );
};
